/**
 * Finding the card in a photograph, and putting it back on the printed scale.
 *
 * The classifier used to be handed a card-relative rectangle and told the capture
 * guide had made it one. That assumption is the reason this module exists: a
 * photograph is a card somewhere in a bigger frame, held at an angle, lit unevenly,
 * and the pass-3 measurement showed what the assumption costs - an art bottom
 * reported at 0.86-0.89 of the card where the picture ends near 0.54.
 *
 * Two steps, both measured rather than assumed:
 *
 *  - **Find the card.** The capture is thresholded against its own background -
 *    estimated per row from the margins the card does not touch - which makes the
 *    detection immune to the lighting gradient across a desk. The thresholded mask
 *    is opened to drop a few stray pixels, and the card's four corners are the
 *    mask's extremes along the two diagonals. That is exact for a convex
 *    quadrilateral, which is what a card is, and it needs no line fitting, no
 *    Hough transform and no iteration.
 *  - **Warp it onto the card's own coordinates.** The four corners go into one
 *    homography (see `geometry.ts`), so rotation and perspective are removed by the
 *    same arithmetic and the result is a card-shaped `FrameImage` the rest of the
 *    pipeline already understands.
 *
 * `renderCapture` is the inverse operation, and it is here rather than in the
 * tests for one reason: a detector has to be measured against a capture whose card
 * position is *known exactly*, and the only way to know it exactly is to have
 * placed it. It composes a card into a captured scene - margins, rotation,
 * keystone, a lighting gradient, sensor noise - from a seed, so the same capture
 * can be rebuilt from a quoted number. The pixels of the card in it are real; the
 * geometry and the lighting are the model, and the ADR says so in those words.
 */
import { assertGreyImage, bilinear, normalizeExposure, solveHomography, warpQuad, } from './geometry.js';
/** The card's printed shape: 2.5 x 3.5 inches. */
export const CARD_ASPECT = 63 / 88;
/**
 * A capture worth testing against: the card is not centred, the hand was turned,
 * the lens keystoned it and the lamp is to one side.
 *
 * These are not flattering values. Pass 3's photographs were card-cropped by
 * construction, and the first thing a real capture does is break that.
 */
export const DEFAULT_CAPTURE = {
    width: 640,
    height: 860,
    cardWidth: 0.5,
    centre: { x: 0.53, y: 0.48 },
    rotation: 8,
    perspective: 0.05,
    background: 92,
    backgroundTexture: 4,
    lighting: { x: 26, y: 12 },
    noise: 3,
    seed: 11,
};
export const describeCapture = (geometry) => `card ${(geometry.cardWidth * 100).toFixed(0)}% of a ${geometry.width}x${geometry.height} frame, ` +
    `centre (${geometry.centre.x}, ${geometry.centre.y}), rotation ${geometry.rotation}deg, ` +
    `keystone ${(geometry.perspective * 100).toFixed(0)}%, lighting ${geometry.lighting.x}/${geometry.lighting.y} levels, ` +
    `noise ${geometry.noise} levels, surface ${geometry.background}+-${geometry.backgroundTexture}`;
/** Deterministic pseudo-random in [0, 1), so a capture is reproducible from its seed. */
function makeRandom(seed) {
    let state = (seed >>> 0) || 1;
    return () => {
        state = (state * 1664525 + 1013904223) >>> 0;
        return state / 0x1_0000_0000;
    };
}
const clamp255 = (value) => (value < 0 ? 0 : value > 255 ? 255 : value);
/** Where the card's four corners end up in a capture built to this geometry. */
export function captureQuad(geometry, cardAspect = CARD_ASPECT) {
    const cardWidth = geometry.cardWidth * geometry.width;
    const cardHeight = cardWidth / cardAspect;
    const centre = { x: geometry.centre.x * geometry.width, y: geometry.centre.y * geometry.height };
    const angle = (geometry.rotation * Math.PI) / 180;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const corners = [
        { x: -cardWidth / 2, y: -cardHeight / 2, top: true },
        { x: cardWidth / 2, y: -cardHeight / 2, top: true },
        { x: cardWidth / 2, y: cardHeight / 2, top: false },
        { x: -cardWidth / 2, y: cardHeight / 2, top: false },
    ];
    return corners.map((corner) => {
        // Keystone: the top edge is narrowed (or widened) about the card's centre, which
        // is what a lens does when the card is not parallel to the sensor.
        const scale = geometry.perspective === 0 ? 1 : 1 + (corner.top ? geometry.perspective : -geometry.perspective);
        const x = corner.x * scale;
        const y = corner.y;
        return {
            x: centre.x + x * cos - y * sin,
            y: centre.y + x * sin + y * cos,
        };
    });
}
/**
 * Compose a card into a captured scene.
 *
 * The card is mapped by a homography from its own unit square to the capture quad,
 * so the same arithmetic that straightens a capture builds one - if the mapping is
 * wrong, the round trip says so.
 */
export function renderCapture(card, geometry = DEFAULT_CAPTURE, cardAspect = card.width / card.height) {
    assertGreyImage(card, 'the card to compose into a capture');
    const quad = captureQuad(geometry, cardAspect);
    const unit = [
        { x: 0, y: 0 },
        { x: 1, y: 0 },
        { x: 1, y: 1 },
        { x: 0, y: 1 },
    ];
    const toCard = solveHomography(quad, unit);
    const random = makeRandom(geometry.seed);
    const levels = new Uint8Array(geometry.width * geometry.height);
    for (let y = 0; y < geometry.height; y += 1) {
        for (let x = 0; x < geometry.width; x += 1) {
            const u = (x + 0.5) / geometry.width;
            const v = (y + 0.5) / geometry.height;
            const source = {
                x: toCard[0] * (x + 0.5) + toCard[1] * (y + 0.5) + toCard[2],
                y: toCard[3] * (x + 0.5) + toCard[4] * (y + 0.5) + toCard[5],
            };
            const denominator = toCard[6] * (x + 0.5) + toCard[7] * (y + 0.5) + toCard[8];
            const cardX = source.x / denominator;
            const cardY = source.y / denominator;
            let level;
            if (cardX >= 0 && cardX <= 1 && cardY >= 0 && cardY <= 1) {
                level = bilinear(card, cardX * (card.width - 1), cardY * (card.height - 1));
            }
            else {
                level = geometry.background + (random() * 2 - 1) * geometry.backgroundTexture;
            }
            level += (u - 0.5) * geometry.lighting.x + (v - 0.5) * geometry.lighting.y;
            level += (random() * 2 - 1) * geometry.noise;
            levels[y * geometry.width + x] = Math.round(clamp255(level));
        }
    }
    return { image: { levels, width: geometry.width, height: geometry.height }, quad };
}
/**
 * The label map of the mask's connected regions, and the largest of them.
 *
 * The mask says "this pixel is not the surface", which on a real photograph is true of
 * more than the card: the top of a table, a hand at the edge of the frame, the shadow
 * the phone casts. That was not a problem while the corners were read as the *global*
 * extremes of the mask - it was fatal. One bright edge strip in the corner of a phone
 * frame becomes the card's top-left, and the quad that comes back is a parallelogram
 * across the whole image; measured against the solring-eval frames, the corners landed
 * up to 100% of the frame width from the card on two thirds of the frames, and the art
 * window cut from that quad matched the index on 7 of 31 - where the same frames cut
 * from ground-truth corners matched on 31 of 31. The card is one region and it is
 * almost always the biggest, so the extremes are taken over the largest region and
 * nothing else. 4-connectivity, because a card's edge is a continuous run of pixels
 * and a diagonal join would bridge a gap between the card and a shadow.
 */
function largestRegion(mask, width, height) {
    const labels = new Uint16Array(width * height);
    const stack = [];
    let next = 0;
    let bestLabel = 0;
    let bestSize = 0;
    for (let start = 0; start < mask.length; start += 1) {
        if (mask[start] !== 1 || labels[start] !== 0)
            continue;
        // A mask with more than this many separate regions is not a photograph of a card,
        // and the label type is 16 bits so the ids stay addressable.
        if (next >= 65_535)
            break;
        next += 1;
        const label = next;
        let size = 0;
        stack.push(start);
        labels[start] = label;
        while (stack.length > 0) {
            const index = stack.pop();
            size += 1;
            const x = index % width;
            const visit = (neighbour) => {
                if (mask[neighbour] === 1 && labels[neighbour] === 0) {
                    labels[neighbour] = label;
                    stack.push(neighbour);
                }
            };
            if (x > 0)
                visit(index - 1);
            if (x < width - 1)
                visit(index + 1);
            if (index >= width)
                visit(index - width);
            if (index + width < mask.length)
                visit(index + width);
        }
        if (size > bestSize) {
            bestSize = size;
            bestLabel = label;
        }
    }
    return { labels, label: bestLabel, size: bestSize };
}
/**
 * The card's four corners in a capture, or null when there is no card in it.
 *
 * The background is estimated per *row* from the capture's own left and right
 * margins rather than as one number for the whole frame, so a lamp on one side of
 * the desk does not turn half the table into card. Structure (a 5x5 standard
 * deviation) joins the test so a black card on a dark surface is still found: the
 * printed frame has detail where an out-of-focus table does not.
 */
export function findCardQuad(image, options = {}) {
    assertGreyImage(image, 'the capture to search for a card in');
    const threshold = options.threshold ?? 18;
    const structure = options.structure ?? 9;
    const minArea = options.minArea ?? 0.08;
    const { width, height, levels } = image;
    // The card has to leave margins for this to work at all, so the surface's level is
    // read from each row's own left and right edges - and interpolated between them,
    // which is what makes the detector survive a lamp on one side of the desk. A
    // single number per row would sit halfway across the lighting gradient, and the
    // margin on the dark side of the frame would then differ from it by half of that
    // gradient before any card was involved.
    const edge = Math.max(1, Math.round(width * 0.02));
    const medianOf = (values) => {
        const sorted = [...values].sort((a, b) => a - b);
        const middle = sorted.length / 2;
        return sorted.length === 0
            ? 0
            : sorted.length % 2 === 1
                ? sorted[Math.floor(middle)]
                : (sorted[middle - 1] + sorted[middle]) / 2;
    };
    const leftBackground = new Float64Array(height);
    const rightBackground = new Float64Array(height);
    for (let y = 0; y < height; y += 1) {
        const left = [];
        const right = [];
        for (let x = 0; x < edge; x += 1)
            left.push(levels[y * width + x] ?? 0);
        for (let x = width - edge; x < width; x += 1)
            right.push(levels[y * width + x] ?? 0);
        leftBackground[y] = medianOf(left);
        rightBackground[y] = medianOf(right);
    }
    // Smoothed along y, so one row that happens to be crossed by a shadow does not
    // introduce a false edge.
    const smoothColumn = (values) => {
        const out = new Float64Array(height);
        const windowSize = Math.max(1, Math.round(height * 0.02));
        for (let y = 0; y < height; y += 1) {
            let total = 0;
            let count = 0;
            for (let offset = -windowSize; offset <= windowSize; offset += 1) {
                const row = y + offset;
                if (row < 0 || row >= height)
                    continue;
                total += values[row];
                count += 1;
            }
            out[y] = total / Math.max(1, count);
        }
        return out;
    };
    const leftSmoothed = smoothColumn(leftBackground);
    const rightSmoothed = smoothColumn(rightBackground);
    const mask = new Uint8Array(width * height);
    const radius = 2;
    for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
            const level = levels[y * width + x] ?? 0;
            const ramp = leftSmoothed[y] + (rightSmoothed[y] - leftSmoothed[y]) * (x / (width - 1));
            const difference = Math.abs(level - ramp);
            let isCard = difference > threshold;
            if (!isCard && x >= radius && y >= radius && x < width - radius && y < height - radius) {
                // Structure: the mean absolute difference to the 5x5 neighbourhood. A flat
                // surface is a few levels of noise; a printed frame is tens.
                let sum = 0;
                for (let dy = -radius; dy <= radius; dy += 1) {
                    for (let dx = -radius; dx <= radius; dx += 1) {
                        sum += Math.abs((levels[(y + dy) * width + (x + dx)] ?? 0) - level);
                    }
                }
                isCard = sum / 25 > structure;
            }
            mask[y * width + x] = isCard ? 1 : 0;
        }
    }
    // Opening: a mask pixel survives when most of its 5x5 neighbourhood agrees. Without
    // it a single hot pixel in the margins becomes a corner.
    const opened = new Uint8Array(width * height);
    let count = 0;
    for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
            if (mask[y * width + x] !== 1)
                continue;
            let neighbours = 0;
            let total = 0;
            for (let dy = -radius; dy <= radius; dy += 1) {
                const sy = y + dy;
                if (sy < 0 || sy >= height)
                    continue;
                for (let dx = -radius; dx <= radius; dx += 1) {
                    const sx = x + dx;
                    if (sx < 0 || sx >= width)
                        continue;
                    neighbours += mask[sy * width + sx] ?? 0;
                    total += 1;
                }
            }
            if (neighbours / Math.max(1, total) >= 0.6) {
                opened[y * width + x] = 1;
                count += 1;
            }
        }
    }
    if (count < minArea * width * height)
        return null;
    // The corners are the largest region's, not the whole mask's: see `largestRegion`.
    // The area test is the region's own, so a frame full of scattered structure that
    // leaves nothing card-sized in one piece is still "no card found".
    const region = largestRegion(opened, width, height);
    if (region.size < minArea * width * height)
        return null;
    // The corners of a convex quadrilateral are its extremes along the two diagonals,
    // which is exact rather than iterative: for a rectangle turned by less than 45
    // degrees, the greatest `x + y` is the bottom-right corner and the least is the
    // top-left, and the other diagonal gives the other two. So the four extremes come
    // back as (bottom-right, top-right, bottom-left, top-left) and are rotated into the
    // order the rest of the pipeline uses - top-left, top-right, bottom-right,
    // bottom-left. Getting this order wrong is invisible in the corner positions and
    // fatal in the warp: it maps the picture onto a scrambled card.
    const best = [
        { x: 0, y: 0 },
        { x: 0, y: 0 },
        { x: 0, y: 0 },
        { x: 0, y: 0 },
    ];
    const extremes = [-Infinity, -Infinity, -Infinity, -Infinity];
    for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
            if (region.labels[y * width + x] !== region.label)
                continue;
            const scores = [x + y, x - y, -x + y, -x - y];
            for (let index = 0; index < 4; index += 1) {
                const score = scores[index];
                if (score > extremes[index]) {
                    extremes[index] = score;
                    best[index] = { x, y };
                }
            }
        }
    }
    return [best[3], best[1], best[0], best[2]];
}
/** The card the quad encloses, straightened onto the card's own coordinates. */
export function rectifyCard(image, quad, options = {}) {
    const width = options.width ?? 320;
    const aspect = options.aspect ?? CARD_ASPECT;
    const height = Math.max(1, Math.round(width / aspect));
    const warped = warpQuad(image, quad, width, height);
    return (options.normalize ?? true) ? normalizeExposure(warped) : warped;
}
/**
 * From a photograph to a card-relative frame, with the reason when it cannot.
 *
 * A capture with no card in it is a *finding*, not an exception: the scanner shows
 * "no card in frame" and the operator moves the phone, which is exactly the
 * behaviour a guide rectangle was supposed to provide.
 */
export function captureCard(image, options = {}) {
    const quad = findCardQuad(image, options);
    if (quad === null) {
        return { frame: null, quad: null, coverage: 0, notes: ['no card was found in the capture: nothing in it differs from the surface around it'] };
    }
    const bounds = quad.reduce((acc, point) => ({
        x0: Math.min(acc.x0, point.x),
        y0: Math.min(acc.y0, point.y),
        x1: Math.max(acc.x1, point.x),
        y1: Math.max(acc.y1, point.y),
    }), { x0: Infinity, y0: Infinity, x1: -Infinity, y1: -Infinity });
    const coverage = (bounds.x1 - bounds.x0) / image.width;
    const frame = rectifyCard(image, quad, options);
    return { frame, quad, coverage, notes: [] };
}
//# sourceMappingURL=capture.js.map